import React, { useState, useEffect } from "react";
import Sidebar from "../../Components/Sidebar";
import TopNav from "../../Components/TopNav";
import { dashboardDetailsServ } from "../../services/user.service";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const dashboardStyles = `
  .dash-page {
    background: #f5f5f7;
    min-height: 100%;
    padding: 28px;
    font-family: 'Segoe UI', sans-serif;
  }

  /* ── STAT CARDS ─────────────────────────────── */
  .dash-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
    margin-bottom: 24px;
  }
  @media (max-width: 900px) {
    .dash-cards { grid-template-columns: 1fr; }
  }

  .dash-card {
    background: #fff;
    border-radius: 16px;
    padding: 22px;
    border: 1px solid #f0eded;
  }

  .dash-card-top {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 18px;
  }

  .dash-card-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: #fdf2f1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: #a60808;
    flex-shrink: 0;
  }

  .dash-card-val {
    font-size: 28px;
    font-weight: 700;
    color: #1c0a0a;
    line-height: 1;
    font-family: 'Segoe UI', sans-serif;
  }

  .dash-card-lbl {
    font-size: 14px;
    color: #828282;
    margin-top: 4px;
  }

  .dash-chip-row {
    display: flex;
    gap: 10px;
  }

  .dash-chip {
    flex: 1;
    border-radius: 9px;
    padding: 9px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .dash-chip-green { background: #eafaf1; }
  .dash-chip-orange { background: #fff8ee; }

  .dash-chip-label {
    font-size: 13px;
    font-weight: 500;
  }
  .dash-chip-green .dash-chip-label { color: #27ae60; }
  .dash-chip-orange .dash-chip-label { color: #e67e22; }

  .dash-chip-count {
    font-size: 14px;
    font-weight: 700;
  }
  .dash-chip-green .dash-chip-count { color: #27ae60; }
  .dash-chip-orange .dash-chip-count { color: #e67e22; }

  /* ── BOTTOM CHARTS ──────────────────────────── */
  .dash-bottom {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 18px;
  }
  @media (max-width: 900px) {
    .dash-bottom { grid-template-columns: 1fr; }
  }

  .dash-chart-card {
    background: #fff;
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #f0eded;
  }

  .dash-chart-title {
    font-size: 16px;
    font-weight: 600;
    color: #1c0a0a;
    margin-bottom: 20px;
  }

  /* ── BAR CHART ──────────────────────────────── */
  .dash-bars-wrap {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    height: 200px;
  }

  .dash-bar-col {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    cursor: pointer;
  }

  .dash-bar {
    width: 100%;
    border-radius: 7px;
    background: #a60808;
    opacity: 0.82;
    min-height: 6px;
    transition: opacity 0.18s, transform 0.18s;
  }
  .dash-bar-col:hover .dash-bar {
    opacity: 1;
    transform: scaleY(1.03);
    transform-origin: bottom;
  }

  .dash-bar-date {
    font-size: 11px;
    color: #bbb;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    white-space: nowrap;
    line-height: 1;
  }

  /* ── PIE CARD ───────────────────────────────── */
  .dash-pie-card {
    background: #fff;
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #f0eded;
    display: flex;
    flex-direction: column;
  }
`;

const ICONS = [
  "ti-calendar-check",
  "ti-users",
  "ti-box",
];

const COLORS = ["#0088FE", "#00C49F", "#a60808"];

function Dashboard() {
  const [details, setDetails] = useState(null);

  const handleDashboardFunc = async () => {
    try {
      let response = await dashboardDetailsServ();
      setDetails(response?.data?.data);
    } catch (error) {}
  };

  useEffect(() => {
    handleDashboardFunc();
  }, []);

  const staticsArr = [
    {
      title: "Total Bookings",
      count: details?.bookings?.totalBooking ?? "—",
      icon: ICONS[0],
      chips: [
        { label: "Active",    count: details?.bookings?.activeBooking,    type: "green"  },
        { label: "Completed", count: details?.bookings?.bookingCompleted, type: "orange" },
      ],
    },
    {
      title: "Total Users",
      count: details?.users?.totalUser ?? "—",
      icon: ICONS[1],
      chips: [
        { label: "Verified",   count: details?.users?.activeUser,   type: "green"  },
        { label: "Incomplete", count: details?.users?.inactiveUser, type: "orange" },
      ],
    },
    {
      title: "Total Products",
      count: details?.products?.totalProduct ?? "—",
      icon: ICONS[2],
      chips: [
        { label: "Single", count: details?.products?.singleProduct, type: "green"  },
        { label: "Combo",  count: details?.products?.comboProduct,  type: "orange" },
      ],
    },
  ];

  const pieData = [
    { status: "Active",    value: details?.bookings?.activeBooking    ?? 0 },
    { status: "Completed", value: details?.bookings?.bookingCompleted ?? 0 },
    {
      status: "Cancelled",
      value:
        (details?.bookings?.totalBooking ?? 0) -
        (details?.bookings?.activeBooking ?? 0) -
        (details?.bookings?.bookingCompleted ?? 0),
    },
  ];

  const maxBooking = Math.max(
    1,
    ...(details?.last15DaysBookings?.map((v) => v.noOfBookings) ?? [1])
  );

  return (
    <div className="bodyContainer">
      <style>{dashboardStyles}</style>

      <Sidebar selectedMenu="Dashboard" selectedItem="Dashboard" />

      <div className="mainContainer">
        <TopNav pageTitle="Dashboard" pagePath="Home / Dashboard" />

        <div className="dash-page">
          {/* ── Stat Cards ── */}
          <div className="dash-cards">
            {staticsArr.map((v, i) => (
              <div className="dash-card" key={i}>
                <div className="dash-card-top">
                  <div className="dash-card-icon">
                    <i className={`ti ${v.icon}`} aria-hidden="true" />
                  </div>
                  <div>
                    <div className="dash-card-val">{v.count}</div>
                    <div className="dash-card-lbl">{v.title}</div>
                  </div>
                </div>
                <div className="dash-chip-row">
                  {v.chips.map((chip, j) => (
                    <div
                      key={j}
                      className={`dash-chip dash-chip-${chip.type}`}
                    >
                      <span className="dash-chip-label">{chip.label}</span>
                      <span className="dash-chip-count">
                        {chip.count ?? "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ── Charts Row ── */}
          <div className="dash-bottom">
            {/* Bar Chart */}
            <div className="dash-chart-card">
              <div className="dash-chart-title">Last 15 days bookings</div>
              <div className="dash-bars-wrap">
                {details?.last15DaysBookings?.map((v, i) => (
                  <div className="dash-bar-col" key={i}>
                    <div
                      className="dash-bar"
                      style={{
                        height: `${Math.max(
                          4,
                          (v.noOfBookings / maxBooking) * 100
                        )}%`,
                      }}
                    />
                    <div className="dash-bar-date">
                      {v.date} · {v.noOfBookings}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pie Chart */}
            <div className="dash-pie-card">
              <div className="dash-chart-title">Booking status</div>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={3}
                    label={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: "10px",
                      border: "1px solid #f0eded",
                      fontSize: "13px",
                    }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={10}
                    formatter={(value) => (
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;


// import React, { useState, useEffect } from "react";
// import Sidebar from "../../Components/Sidebar";
// import TopNav from "../../Components/TopNav";
// import { dashboardDetailsServ } from "../../services/user.service";
// import { useNavigate } from "react-router-dom";
// import {
//   PieChart,
//   Pie,
//   Cell,
//   Tooltip,
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Legend,
//   ResponsiveContainer,
// } from "recharts";
// import { saveAs } from "file-saver";
// import * as XLSX from "xlsx";
// function Dashboard() {
//   const [statics, setStatics] = useState(null);
//   const navigate = useNavigate();

//   const [details, setDetails] = useState(null);
//   const handleDashboardFunc = async () => {
//     try {
//       let response = await dashboardDetailsServ();
//       setDetails(response?.data?.data);
//     } catch (error) {}
//   };
//   useEffect(() => {
//     handleDashboardFunc();
//   }, []);
//   // Export handler
//   const exportToExcel = () => {
//     const ws = XLSX.utils.json_to_sheet(details?.last15DaysBookings || []);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Bookings");
//     const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//     saveAs(
//       new Blob([wbout], { type: "application/octet-stream" }),
//       "bookings.xlsx"
//     );
//   };
//   const [filter, setFilter] = useState({
//     deliveryType: "",
//     status: "",
//     payment: "",
//   });
  

//   const staticsArr = [
//     {
//       title: "Total Bookings",
//       count: details?.bookings?.totalBooking,
//       bgColor: "#6777EF",
//       img: "https://cdn-icons-png.flaticon.com/128/7322/7322293.png",
//       subMenu: [
//         {
//           title: "Active",
//           count: details?.bookings?.activeBooking,
//           bgColor: "#63ED7A",
//         },
//         {
//           title: "Completed",
//           count: details?.bookings?.bookingCompleted,
//           bgColor: "#FFA426",
//         },
//       ],
//     },
//     {
//       title: "Total Users",
//       count: details?.users?.totalUser,
//       bgColor: "#6777EF",
//       img: "https://cdn-icons-png.flaticon.com/128/1077/1077114.png",
//       subMenu: [
//         {
//           title: "Verified",
//           count: details?.users?.activeUser,
//           bgColor: "#63ED7A",
//         },
//         {
//           title: "Incomplete",
//           count: details?.users?.inactiveUser,
//           bgColor: "#FFA426",
//         },
//       ],
//     },
//     {
//       title: "Total Products",
//       count: details?.products?.totalProduct,
//       bgColor: "#6777EF",
//       img: "https://cdn-icons-png.flaticon.com/128/3514/3514491.png",
//       subMenu: [
//         {
//           title: "Single",
//           count: details?.products?.singleProduct,
//           bgColor: "#63ED7A",
//         },
//         {
//           title: "Combo",
//           count: details?.products?.comboProduct,
//           bgColor: "#FFA426",
//         },
//       ],
//     },
//   ];
//   const COLORS = ["#0088FE", "#00C49F", "#FFBB28"];
//   const data = [
//     { status: "Active", value: details?.bookings?.activeBooking },
//     { status: "Completed", value: details?.bookings?.bookingCompleted },
//     {
//       status: "Cancelled",
//       value: details?.bookings?.totalBooking - details?.bookings?.activeBooking,
//     },
//   ];
//   return (
//     <div className="bodyContainer">
//       <Sidebar selectedMenu="Dashboard" selectedItem="Dashboard" />
//       <div className="mainContainer">
//         <TopNav />
//         <div className="p-lg-4 p-md-3 p-2">
//           <div
//             className="row mx-0 p-0"
//             style={{
//               position: "relative",
//               top: "-75px",
//               marginBottom: "-75px",
//             }}
//           >
//             {staticsArr?.map((v, i) => {
//               return (
//                 <div className="col-md-4 col-12 ">
//                   <div className="topCard shadow-sm py-4 px-3 rounded mb-3">
//                     <div className="d-flex align-items-center ">
//                       <div
//                         className="p-2 shadow rounded"
//                         style={{ background: v?.bgColor }}
//                       >
//                         <img src={v?.img} />
//                       </div>
//                       <div className="ms-3">
//                         <h2 className="text-secondary">{v?.count}</h2>
//                         <h6>{v?.title}</h6>
//                       </div>
//                     </div>
//                     <div className="d-flex justify-content-between mt-3">
//                       {v?.subMenu?.map((subMenu, i) => {
//                         return (
//                           <div
//                             className=" d-flex justify-content-between align-items-center px-2 py-1 text-light"
//                             style={{
//                               borderRadius: "4px",
//                               background: subMenu?.bgColor,
//                               width: "45%",
//                               opacity: "0.9",
//                             }}
//                           >
//                             <h6>{subMenu?.title}</h6>
//                             <p className="mb-0">{subMenu?.count}</p>
//                           </div>
//                         );
//                       })}
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//           <div className="row px-2 mt-2">
//           <div className="col-7">
//   <div className="rounded shadow-sm p-4">
//     <h5>Last 15 Day Bookings</h5>

//     {/* Filter Section */}
//     <div className="d-flex gap-2 mb-2">
//       <select
//         className="form-select"
//         onChange={(e) => setFilter({ ...filter, deliveryType: e.target.value })}
//       >
//         <option value="">All Delivery Types</option>
//         <option value="door">Door</option>
//         <option value="pickup">Pick-up</option>
//       </select>

//       <select
//         className="form-select"
//         onChange={(e) => setFilter({ ...filter, status: e.target.value })}
//       >
//         <option value="">All Status</option>
//         <option value="confirmed">Confirmed</option>
//         <option value="pending">Pending</option>
//         <option value="cancelled">Cancelled</option>
//       </select>

//       <select
//         className="form-select"
//         onChange={(e) => setFilter({ ...filter, payment: e.target.value })}
//       >
//         <option value="">All Payments</option>
//         <option value="paid">Paid</option>
//         <option value="to-pay">To-Pay</option>
//       </select>
//     </div>

//     {/* Chart */}
//     <ResponsiveContainer width="100%" height={300}>
//       <BarChart data={details?.last15DaysBookings}>
//         <CartesianGrid strokeDasharray="3 3" />
//         <XAxis dataKey="date" />
//         <YAxis />
//         <Tooltip 
//           formatter={(value, name) => {
//             if (name === "totalBookings") return [`${value} orders`, "Orders"];
//             if (name === "totalRevenue") return [`Rs ${value}`, "Revenue"];
//             return value;
//           }}
//         />
//         <Legend />
//         <Bar
//           dataKey="totalBookings"
//           fill="#139F01"
//           name="Bookings"
//           onClick={(data) => navigate(`/all-booking/${data.mongoDate}`)}
//         />
//         <Bar dataKey="totalRevenue" fill="#8884d8" name="Revenue" />
//       </BarChart>
//     </ResponsiveContainer>
//   </div>
// </div>


//             <div className="col-5 ">
//               <ResponsiveContainer width="100%" height={300}>
//                 <PieChart>
//                   <Pie
//                     data={data}
//                     dataKey="value"
//                     nameKey="status"
//                     cx="50%"
//                     cy="50%"
//                     outerRadius={100}
//                     fill="#8884d8"
//                     label
//                   >
//                     {data.map((entry, index) => (
//                       <Cell
//                         key={`cell-${index}`}
//                         fill={COLORS[index % COLORS.length]}
//                       />
//                     ))}
//                   </Pie>
//                   <Tooltip />
//                   <Legend />
//                 </PieChart>
//               </ResponsiveContainer>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Dashboard;










// backend data 
// bookingsLast15Days.push({
//   date: formattedDate,
//   mongoDate: mongoDate,
//   totalBookings: bookingData ? bookingData.totalBookings : 0,
//   totalRevenue: bookingData ? bookingData.totalRevenue : 0,
//   details: bookingData ? bookingData.details : [],
// });

//  const last15Days = await Booking.aggregate([
//       {
//         $match: {
//           createdAt: { $gte: moment().subtract(15, "days").startOf("day").toDate() },
//         },
//       },
//       {
//         $group: {
//           _id: {
//             date: { $dateToString: { format: "%Y-%m-%d", date: "$createdAt" } },
//             deliveryType: "$deliveryType",
//             orderStatus: "$status",
//             paymentStatus: "$paymentStatus",
//           },
//           count: { $sum: 1 },
//           revenue: { $sum: "$totalAmount" },
//         },
//       },
//       {
//         $group: {
//           _id: "$_id.date",
//           totalBookings: { $sum: "$count" },
//           totalRevenue: { $sum: "$revenue" },
//           details: {
//             $push: {
//               deliveryType: "$_id.deliveryType",
//               orderStatus: "$_id.orderStatus",
//               paymentStatus: "$_id.paymentStatus",
//               count: "$count",
//               revenue: "$revenue",
//             },
//           },
//         },
//       },
//       { $sort: { _id: 1 } },
//     ]);